import { ChristmasTree } from "./ChristmasTree";
import { Link } from "@tanstack/react-router";
import { Building2, Hash, MapPin, Mail, Phone } from "lucide-react";
import logo from "@/assets/logo.webp";
import logoWhite from "@/assets/logo-white.webp";
import { openCookieSettings } from "@/components/storefront/cookie-consent";
import { NotificationToggle } from "@/components/storefront/NotificationToggle";
import { DeferredMount } from "@/components/storefront/DeferredMount";
import { TrustBadges } from "@/components/storefront/TrustBadges";

export function Footer() {
  return (
    <footer className="xmas-footer relative mt-8 border-t border-border lg:[&_li>a]:min-h-7 lg:[&_li>button]:min-h-7">
      <ChristmasTree className="pointer-events-none absolute top-4 right-4 hidden h-44 w-auto lg:block" />
      <div className="mx-auto grid max-w-none gap-5 px-4 py-5 sm:grid-cols-2 lg:grid-cols-4 lg:pr-36">
        <div className="min-w-0">
          <Link to="/" className="mb-2 inline-flex items-center gap-2">
            <img src={logo} alt="MrPanda.gr" width={360} height={95} className="h-10 w-[152px] object-contain object-left dark:hidden" />
            <img src={logoWhite} alt="MrPanda.gr" width={360} height={95} className="hidden h-10 w-[152px] object-contain object-left dark:block" />
            <span className="text-2xl animate-bob" aria-hidden>🐼</span>
          </Link>
          <p className="max-w-sm text-sm leading-snug text-muted-foreground">
            Χιλιάδες προϊόντα στις καλύτερες τιμές, με γρήγορη παράδοση σε όλη την Ελλάδα.
          </p>
          <DeferredMount phase={2}>
            <NotificationToggle />
          </DeferredMount>
          <div className="mt-2 flex flex-col gap-0 border-l-2 border-primary/25 pl-2 text-sm text-foreground lg:text-xs xl:text-sm">
            <a href="mailto:info@mrpanda.gr" className="group inline-flex min-h-11 w-fit max-w-full items-center gap-2 py-1 font-medium transition-colors hover:text-primary lg:min-h-7">
              <Mail className="h-4 w-4 shrink-0 text-primary" />
              <span className="min-w-0 break-all">info@mrpanda.gr</span>
            </a>
            <a href="tel:2311822500" className="group inline-flex min-h-11 w-fit max-w-full items-center gap-2 py-1 font-medium transition-colors hover:text-primary lg:min-h-7">
              <Phone className="h-4 w-4 shrink-0 text-primary" />
              <span>2311 822 500</span>
            </a>
          </div>
        </div>
        <div>
          <h3 className="mb-2 text-sm font-semibold">Εξυπηρέτηση</h3>
          <ul className="space-y-0 text-sm text-muted-foreground">
            <li><Link to="/contact" className="inline-flex min-h-11 items-center hover:text-foreground">Επικοινωνία</Link></li>
            <li><Link to="/about" className="inline-flex min-h-11 items-center hover:text-foreground">Σχετικά με εμάς</Link></li>
            <li><Link to="/account" className="inline-flex min-h-11 items-center hover:text-foreground">Ο λογαριασμός μου</Link></li>
            <li><Link to="/account/orders" className="inline-flex min-h-11 items-center hover:text-foreground">Οι παραγγελίες μου</Link></li>
            <li><Link to="/account/addresses" className="inline-flex min-h-11 items-center hover:text-foreground">Οι διευθύνσεις μου</Link></li>
            <li><Link to="/wishlist" className="inline-flex min-h-11 items-center hover:text-foreground">Αγαπημένα προϊόντα</Link></li>
            <li><Link to="/account/privacy" className="inline-flex min-h-11 items-center hover:text-foreground">Απόρρητο &amp; Δεδομένα</Link></li>
          </ul>
        </div>
        <div>
          <h3 className="mb-2 text-sm font-semibold">Πληροφορίες</h3>
          <ul className="space-y-0 text-sm text-muted-foreground">
            <li><Link to="/terms" className="inline-flex min-h-11 items-center hover:text-foreground">Όροι χρήσης</Link></li>
            <li><Link to="/returns" className="inline-flex min-h-11 items-center hover:text-foreground">Ακυρώσεις & Επιστροφές</Link></li>
            <li><Link to="/privacy" className="inline-flex min-h-11 items-center hover:text-foreground">Πολιτική απορρήτου</Link></li>
            <li><Link to="/cookies" className="inline-flex min-h-11 items-center hover:text-foreground">Πολιτική cookies</Link></li>
            <li>
              <button type="button" onClick={openCookieSettings} className="inline-flex min-h-11 items-center hover:text-foreground">
                Ρυθμίσεις cookies
              </button>
            </li>
            <li><Link to="/shipping-methods" className="inline-flex min-h-11 items-center hover:text-foreground">Τρόποι αποστολής</Link></li>
            <li><Link to="/payment-methods" className="inline-flex min-h-11 items-center hover:text-foreground">Τρόποι πληρωμής</Link></li>
          </ul>
        </div>
        <div>
          <h3 className="mb-2 text-sm font-semibold">Στοιχεία επιχείρησης</h3>
          <ul className="space-y-1.5 text-sm leading-snug text-muted-foreground">
            <li className="flex items-start gap-2.5">
              <Building2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" />
              <span>
                <span className="block text-xs uppercase tracking-wide text-muted-foreground">Επωνυμία</span>
                <span className="font-medium text-foreground">ΑΣΛΑΝΟΓΛΟΥ ΑΙΚΑΤΕΡΙΝΗ ΒΑΣΙΛΕΙΟΣ</span>
              </span>
            </li>
            <li className="flex items-start gap-2.5">
              <Hash className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" />
              <span>
                <span className="block text-xs uppercase tracking-wide text-muted-foreground">Αριθμός Γ.Ε.ΜΗ.</span>
                <span className="font-medium text-foreground">156171904000</span>
              </span>
            </li>
            <li className="flex items-start gap-2.5">
              <Hash className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" />
              <span>
                <span className="block text-xs uppercase tracking-wide text-muted-foreground">ΑΦΜ</span>
                <span className="font-medium text-foreground">117875310</span>
              </span>
            </li>
            <li className="flex items-start gap-2.5">
              <Hash className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" />
              <span>
                <span className="block text-xs uppercase tracking-wide text-muted-foreground">ΔΟΥ</span>
                <span className="font-medium text-foreground">ΚΕΦΟΔΕ ΘΕΣΣΑΛΟΝΙΚΗΣ</span>
              </span>
            </li>
            <li className="flex items-start gap-2.5">
              <MapPin className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" />
              <span>
                <span className="block text-xs uppercase tracking-wide text-muted-foreground">Διεύθυνση</span>
                <span className="font-medium text-foreground">Π. Λεβαντή 15Α, 564 29 Σταυρούπολη Θεσσαλονίκης, Ελλάδα</span>
              </span>
            </li>
          </ul>
        </div>
      </div>
      <div className="border-t border-border px-4 py-3">
        <TrustBadges compact className="mx-auto max-w-4xl" />
      </div>
      <div className="border-t border-border px-4 pt-2 pb-[calc(5rem+var(--xmas-drift-space,0px))] text-center text-xs text-muted-foreground sm:pb-[calc(0.5rem+var(--xmas-drift-space,0px))]">
        <span>Φτιαγμένο με 💛 από την παρέα του Mr Panda 🐼</span>
        <span className="mx-1.5 hidden sm:inline">·</span>
        <span className="block sm:inline">© {new Date().getFullYear()} MrPanda.gr — Όλα τα δικαιώματα κατοχυρωμένα.</span>
        <span className="mx-1.5 hidden sm:inline">·</span>
        <span className="block text-[10px] sm:inline">v{typeof __APP_VERSION__ !== "undefined" ? __APP_VERSION__ : "dev"}</span>
      </div>
    </footer>
  );
}